import type { ColDef, ColGroupDef, GridOptions } from '@ag-grid-community/core';
import type {
  AggregateReportConfig,
  ReportData,
} from './report-viewer/report-viewer.model';

/**
 * Report Metadata Configuration
 * Centralized location for all report titles and metadata
 */
export const REPORT_METADATA = {
  'model-aue': {
    title: 'Model/AUE by OU Report',
  },
  'chrome-version-age': {
    title: 'Chrome Version Age by OU Report',
  },
  'battery-health': {
    title: 'Battery Health by OU Report',
  },
  'storage-by-ou': {
    title: 'Storage by OU Report',
  },
  'fifteen-day-activity': {
    title: '15-Day Activity by OU Report',
  },
  SINGLE_USER: {
    title: 'User Report',
  },
} as const;

/**
 * User Report Column Definitions
 * Column configuration for user device session reports with Gopher Buddy and Admin Console data
 */
export const USER_REPORT_COLUMN_DEFS: (ColDef | ColGroupDef)[] = [
  // Serial Number - hidden but used for row grouping
  {
    headerName: 'Serial Number',
    field: 'serialNumber',
    rowGroup: true,
    hide: true,
  },
  // Reported by Gopher Buddy section
  {
    headerName: 'Reported by Gopher Buddy',
    headerClass: 'custom-header',
    children: [
      {
        headerName: 'Session Start',
        field: 'sessionStart',
        width: 170,
        headerClass: 'custom-header',
        cellClass: 'custom-cell',
      },
      {
        headerName: 'Session Duration',
        field: 'sessionDuration',
        width: 100,
        headerClass: 'custom-header',
        cellClass: 'custom-cell',
      },
      {
        headerName: 'Session Private IP',
        field: 'sessionPrivateIp',
        width: 140,
        headerClass: 'custom-header',
        cellClass: 'custom-cell',
      },
      {
        headerName: 'Session Public IP',
        field: 'sessionPublicIp',
        width: 140,
        headerClass: 'custom-header',
        cellClass: 'custom-cell',
      },
      {
        headerName: 'OS Version',
        field: 'osVersion',
        width: 150,
        headerClass: 'custom-header',
        cellClass: 'custom-cell',
      },
      {
        headerName: 'Device OU Path',
        field: 'deviceOuPath',
        width: 150,
        headerClass: 'custom-header',
        cellClass: 'custom-cell',
      },
    ],
  },
  // Admin Console Values section
  {
    headerName: 'Admin Console Values',
    headerClass: 'custom-header',
    children: [
      {
        headerName: 'Recentness of use',
        field: 'recentnessOfUse',
        width: 130,
        headerClass: 'custom-header',
        cellClass: 'custom-cell',
      },
      {
        headerName: 'OU Path',
        field: 'ouPath',
        width: 250,
        headerClass: 'custom-header',
        cellClass: 'custom-cell',
      },
      {
        headerName: 'Annotated User',
        field: 'annotatedUser',
        width: 200,
        headerClass: 'custom-header',
        cellClass: 'custom-cell',
      },
      {
        headerName: 'Annotated Location',
        field: 'annotatedLocation',
        width: 200,
        headerClass: 'custom-header',
        cellClass: 'custom-cell',
      },
    ],
  },
];

/**
 * User Report Grid Options
 * Grid configuration for user device session reports with row grouping
 */
export const USER_REPORT_GRID_OPTIONS: GridOptions = {
  animateRows: true,
  groupDefaultExpanded: 0, // Start collapsed
  defaultColDef: {
    resizable: true,
    sortable: true,
    wrapHeaderText: true,
    autoHeaderHeight: true,
  },
  overlayNoRowsTemplate:
    '<span class="ag-overlay-no-rows-center">No Gopher Buddy sessions found for this user</span>',
  autoGroupColumnDef: {
    headerName: 'Serial Number',
    width: 250,
    pinned: 'left',
    lockPosition: true,
    cellRendererParams: {
      suppressCount: true,
    },
    headerClass: 'serial-number-header',
    cellClass: 'serial-number-cell',
    suppressColumnsToolPanel: true,
  },
  suppressAggFuncInHeader: true,
};

/**
 * Mock Data for Model/AUE by OU Report
 * TODO: Replace with actual API call
 */
export const MODEL_AUE_BY_OU_MOCK_DATA = [
  {
    ou: 'Engineering',
    aue: '2025-06',
    model: 'HP Chromebook 14',
    deviceCount: 45,
    status: 'ACTIVE',
  },
  {
    ou: 'Engineering',
    aue: '2025-06',
    model: 'Dell Chromebook 3100',
    deviceCount: 32,
    status: 'ACTIVE',
  },
  {
    ou: 'Engineering',
    aue: '2025-12',
    model: 'HP Chromebook 14',
    deviceCount: 18,
    status: 'ACTIVE',
  },
  {
    ou: 'Engineering',
    aue: '2025-12',
    model: 'Acer Chromebook 314',
    deviceCount: 25,
    status: 'DISABLED',
  },
  {
    ou: 'Sales',
    aue: '2025-06',
    model: 'HP Chromebook 14',
    deviceCount: 67,
    status: 'ACTIVE',
  },
  {
    ou: 'Sales',
    aue: '2025-06',
    model: 'Lenovo Chromebook C340',
    deviceCount: 43,
    status: 'ACTIVE',
  },
  {
    ou: 'Sales',
    aue: '2025-12',
    model: 'Dell Chromebook 3100',
    deviceCount: 29,
    status: 'DISABLED',
  },
  {
    ou: 'Marketing',
    aue: '2025-06',
    model: 'Acer Chromebook 314',
    deviceCount: 34,
    status: 'ACTIVE',
  },
  {
    ou: 'Marketing',
    aue: '2025-12',
    model: 'HP Chromebook 14',
    deviceCount: 21,
    status: 'ACTIVE',
  },
  {
    ou: 'Marketing',
    aue: '2025-12',
    model: 'Lenovo Chromebook C340',
    deviceCount: 16,
    status: 'INACTIVE',
  },
];

/**
 * Configuration for Model/AUE by OU aggregate report
 */
export function createModelAueByOuReportConfig(data: unknown[]): ReportData {
  const aggregateConfig: AggregateReportConfig = {
    columns: [
      {
        headerName: 'OU / Auto-Update Expiration (AUE) / Model',
        field: 'groupHierarchy',
      },
      {
        headerName: 'Number of Devices',
        field: 'deviceCount',
        flex: 1,
        aggFunc: 'sum',
      },
    ],
    groupFields: ['ou', 'aue', 'model'],
    aggregationField: 'deviceCount',
    autoGroupColumnName: 'OU / Auto-Update Expiration (AUE) / Model',
    data,
  };

  return {
    type: 'model-aue',
    data,
    aggregateConfig,
  };
}

/**
 * Configuration for Storage by OU aggregate report
 * TODO: Implement when storage by OU report is added
 */
export function createStorageByOuReportConfig(data: unknown[]): ReportData {
  const aggregateConfig: AggregateReportConfig = {
    columns: [
      {
        headerName: 'Total Storage (GB)',
        field: 'storageAmount',
        flex: 1,
        aggFunc: 'sum',
      },
    ],
    groupFields: ['ou'],
    aggregationField: 'storageAmount',
    autoGroupColumnName: 'OU Path',
    data,
  };

  return {
    type: 'storage-by-ou',
    data,
    aggregateConfig,
  };
}

/**
 * Configuration for Chrome Version aggregate report
 * TODO: Implement when chrome version report is added
 */
export function createChromeVersionReportConfig(data: unknown[]): ReportData {
  const aggregateConfig: AggregateReportConfig = {
    columns: [
      {
        headerName: 'Number of Devices',
        field: 'deviceCount',
        flex: 1,
        aggFunc: 'sum',
      },
    ],
    groupFields: ['chromeVersion'],
    aggregationField: 'deviceCount',
    autoGroupColumnName: 'Chrome Version',
    data,
  };

  return {
    type: 'chrome-version-age',
    data,
    aggregateConfig,
  };
}

/**
 * Configuration for Battery Health aggregate report
 * TODO: Implement when battery health report is added
 */
export function createBatteryHealthReportConfig(data: unknown[]): ReportData {
  const aggregateConfig: AggregateReportConfig = {
    columns: [
      {
        headerName: 'Number of Devices',
        field: 'deviceCount',
        flex: 1,
        aggFunc: 'sum',
      },
    ],
    groupFields: ['batteryStatus', 'model'],
    aggregationField: 'deviceCount',
    autoGroupColumnName: 'Battery Status / Model',
    data,
  };

  return {
    type: 'battery-health',
    data,
    aggregateConfig,
  };
}

/**
 * Configuration for 15-Day Activity aggregate report
 * TODO: Implement when 15-day activity report is added
 */
export function createFifteenDayActivityReportConfig(
  data: unknown[]
): ReportData {
  const aggregateConfig: AggregateReportConfig = {
    columns: [
      {
        headerName: 'Number of Devices',
        field: 'deviceCount',
        flex: 1,
        aggFunc: 'sum',
      },
    ],
    groupFields: ['activityStatus', 'ou'],
    aggregationField: 'deviceCount',
    autoGroupColumnName: 'Activity Status / OU',
    data,
  };

  return {
    type: 'fifteen-day-activity',
    data,
    aggregateConfig,
  };
}
